import { closeSocket } from "@rentiohq/shared-frontend/dist/redux/socket/socket.actions";
import * as chatActions from "@rentiohq/shared-frontend/dist/reduxV2/chats/chat.actions";
import * as chatSelectors from "@rentiohq/shared-frontend/dist/reduxV2/chats/chat.selectors";
import { EChatMemberTypes } from "@rentiohq/shared-frontend/dist/types/chat.types";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { usePrevious } from "@rentiohq/shared-frontend/dist/utils/hooks.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getMembersWithOneOfRoles } from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import { MuiDialog, Stack } from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { ContactCard } from "../../../../components/ContactCard";
import { OpenAppCard } from "../../../../components/OpenAppCard";
import { ContactMethods } from "../components/ContactMethods";
import { ProblemCategories } from "../components/ProblemCategories";
import { ReportProblemForm } from "../components/ReportProblemForm";
import { EContactMethod, EProblemCategory } from "../types";

interface IProps {
  property: IProperty;
  openDialog: boolean;
  toggleOpenDialog: () => void;
}

export const adminRoles = [
  EPropertyMemberTypes.IndexationManager,
  EPropertyMemberTypes.FinancialManager,
  EPropertyMemberTypes.TechnicalManager,
];

export const ContactManager = ({
  property,
  openDialog,
  toggleOpenDialog,
}: IProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [category, setCategory] = React.useState<EProblemCategory>();
  const [method, setMethod] = React.useState<EContactMethod>();
  const [step, setStep] = React.useState(1);

  const newChat = useSelector((state: IRootStore) =>
    chatSelectors.getNewCreatedChat(state),
  );

  const isCreatingChat = useSelector((state: IRootStore) =>
    chatSelectors.isCreating(state),
  );

  const createChatError = useSelector((state: IRootStore) =>
    chatSelectors.createError(state),
  );

  const prevIsCreatingChat = usePrevious(isCreatingChat);

  React.useEffect(() => {
    if (prevIsCreatingChat && !isCreatingChat) {
      if (createChatError) {
        enqueueSnackbar({
          message: getLocalizedText("system.error"),
          variant: "error",
        });
        handleClose();
      }
      if (newChat?.id) {
        dispatch(
          closeSocket.actions.start({
            autoReopenAfterClose: false,
            sendPendingMessagesOnNextOpen: true,
          }),
        );
        navigate(`/chats/${newChat.id}`);
        handleClose();
      }
    }
  }, [newChat, createChatError, isCreatingChat, prevIsCreatingChat]);

  const handleCreateChat = () => {
    const members =
      getManagerId().map((contactId: number) => ({
        accountId: contactId,
        roles: [EChatMemberTypes.Participant],
      })) || [];

    const isGroupChat = members.length > 1;
    const isFinancialIssue = category === EProblemCategory.Payment;

    const chatInfo = {
      name: "Chat",
      propertyId: property.id,
      members,
    };

    if (isGroupChat) {
      chatInfo.name = isFinancialIssue
        ? getLocalizedText("system.issue_types.financial_issues")
        : getLocalizedText("system.issue_types.technical_issues");
    }

    dispatch(
      chatActions.createStart.getAction({
        data: chatInfo,
      }),
    );
  };

  const handleSelectCategory = (choice: EProblemCategory) =>
    setCategory(choice);

  const handleSelectMethod = (choice: EContactMethod) => {
    if (choice === EContactMethod.Chat) {
      handleCreateChat();
    } else {
      setMethod(choice);
    }
  };

  const goToNextStep = () => setStep(prevState => prevState + 1);

  const goToPreviousStep = () => setStep(prevState => prevState - 1);

  const resetStates = () => {
    setCategory(undefined);
    setMethod(undefined);
    setStep(1);
  };

  const handleClose = () => {
    toggleOpenDialog();
    resetStates();
  };

  const getStepActions = () => {
    if (step === 2 && category === EProblemCategory.RentioApp) {
      return [];
    }

    switch (step) {
      case 2:
        if (category === EProblemCategory.RentioApp) {
          return [
            {
              variant: EButtonVariants.Outlined,
              onClick: goToPreviousStep,
              label: getLocalizedText("system.previous"),
            },
            {
              onClick: handleClose,
              label: getLocalizedText("system.ok"),
            },
          ];
        }
        return [
          {
            variant: EButtonVariants.Outlined,
            onClick: goToPreviousStep,
            label: getLocalizedText("system.previous"),
          },
          { onClick: goToNextStep, label: getLocalizedText("system.next") },
        ];
      case 3:
        return [
          {
            variant: EButtonVariants.Outlined,
            onClick: goToPreviousStep,
            label: getLocalizedText("system.previous"),
          },
          { onClick: handleClose, label: getLocalizedText("system.ok") },
        ];
      default:
        return [
          {
            variant: EButtonVariants.Outlined,
            onClick: handleClose,
            label: getLocalizedText("system.cancel"),
          },
          { onClick: goToNextStep, label: getLocalizedText("system.next") },
        ];
    }
  };

  const getDialogTitle = () => {
    if (step > 1 && category === EProblemCategory.RentioApp) {
      return "nav.report.problem";
    } else {
      return "property.report_problem.contact.manager";
    }
  };

  const getAdminRoles = React.useMemo(() => {
    if (category === EProblemCategory.Payment) {
      return [EPropertyMemberTypes.FinancialManager];
    }

    if (category === EProblemCategory.Property) {
      return [EPropertyMemberTypes.TechnicalManager];
    }

    return [];
  }, [category]);

  const propertyAdmins = getMembersWithOneOfRoles(
    property?.members,
    getAdminRoles,
  );

  const choices = [
    {
      icon: EAssets.ShedIcon,
      label: getLocalizedText("property.contact_manager.choice.property"),
      value: EProblemCategory.Property,
    },
    {
      icon: EAssets.EuroIcon,
      label: getLocalizedText("property.contact_manager.choice.payment"),
      value: EProblemCategory.Payment,
    },
    {
      icon: EAssets.ApplicationIcon,
      label: getLocalizedText("property.contact_manager.choice.rentio_app"),
      value: EProblemCategory.RentioApp,
    },
  ];

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <ProblemCategories
            // @ts-ignore
            choices={choices}
            category={category}
            handleSelect={handleSelectCategory}
            title="property.contact_manager.select_reason.title"
          />
        );
      case 2:
        switch (category) {
          case EProblemCategory.Payment:
          case EProblemCategory.Property:
            return (
              <ContactMethods
                method={method}
                handleSelect={handleSelectMethod}
              />
            );
          case EProblemCategory.RentioApp:
            return (
              <ReportProblemForm
                property={property}
                handleClose={handleClose}
              />
            );
          default:
            return <OpenAppCard />;
        }
      case 3:
        return (
          method === EContactMethod.Call && (
            <Stack flexDirection="row" gap={1} flexWrap="wrap">
              {propertyAdmins.map(admin => (
                <ContactCard
                  key={admin?.account?.id}
                  account={admin?.account}
                  propertyId={property?.id}
                />
              ))}
            </Stack>
          )
        );
    }
  };

  const getManagerId = () => {
    if (category === EProblemCategory.Payment) {
      const financialManagers = property.members.filter(member =>
        member.roles.includes(EPropertyMemberTypes.FinancialManager),
      );

      return financialManagers.map(manager => manager.account.id);
    }

    if (category === EProblemCategory.Property) {
      const technicalManager = property.members.filter(member =>
        member.roles.includes(EPropertyMemberTypes.TechnicalManager),
      );

      return technicalManager.map(manager => manager.account.id);
    }

    return [];
  };

  return (
    <MuiDialog
      title={getLocalizedText(getDialogTitle())}
      open={openDialog}
      toggleOpen={handleClose}
      actions={getStepActions()}
      maxWidth={step === 3 ? "unset" : "600px"}
    >
      {renderStepContent()}
    </MuiDialog>
  );
};
